import React, { ReactNode } from "react";
import { Col, Container, Row, Spinner, Alert, Button, Form, Dropdown } from "react-bootstrap";
import { Sun, Moon, Type, Hash, BookHalf } from "react-bootstrap-icons";
import { getAnalyticsWidget, getAnalyticsFilterValues, searchAnalyticsFilters, createExportSchedule } from "../../../utils/Requests";
import { getWidgetTheme, getDefaultDateRange } from "../../../utils/analytics";
import { MetricConfig, AnalyticsFilters, DocsSidebarEntry } from "../../../types/analytics";
import { InteractiveLineChart, ExportButton, ScheduleExportModal, DocsSidebar, WidgetCard, PopPeriodBanner } from "../../../components/analytics";
import MetricCards, { MetricCardItem } from "../../../components/analytics/MetricCards";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";


type FilterOption = string | { label: string; brandId: string };

type MultiSelectDropdownProps = {
    label: string;
    selectedValues: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    customerConfig: CustomerConfig;
    filterType: string;
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    selectedValues,
    onChange,
    placeholder = "Select...",
    customerConfig,
    filterType
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [options, setOptions] = React.useState<FilterOption[]>([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchById, setSearchById] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const getDisplayLabel = (): string => {
        if (filterType === "brand_id") {
            return searchById ? "Brand ID" : "Brand Name";
        }
        return label;
    };

    const getSearchPlaceholder = (): string => {
        if (filterType === "brand_id") {
            return searchById ? "Search by ID..." : "Search by Name...";
        }
        return "Search...";
    };

    const getOptionValue = (option: FilterOption): string => {
        return typeof option === "string" ? option : option.brandId;
    };

    const getOptionDisplay = (option: FilterOption): string => {
        if (typeof option === "string") {
            return option;
        }
        return `${option.label} (${option.brandId})`;
    };

    const findOptionByValue = (value: string): FilterOption | undefined => {
        return options.find(opt => getOptionValue(opt) === value);
    };

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadFilterValues = React.useCallback(async (page: number): Promise<void> => {
        try {
            setIsLoading(true);
            const response = await getAnalyticsFilterValues(customerConfig, filterType, page, searchById);

            const newValues: FilterOption[] = Array.isArray(response)
                ? response.map(item =>
                    typeof item === "string" ? item : { label: item.label, brandId: item.brand_id }
                  )
                : [];

            if (page === 1) {
                setOptions(newValues);
            } else {
                setOptions(prev => [...prev, ...newValues]);
            }

            setHasMore(newValues.length === 20);
            setCurrentPage(page);
        } catch (error) {
            Logger.error("Error loading filter values", error);
        } finally {
            setIsLoading(false);
        }
    }, [customerConfig, filterType, searchById]);

    const handleSearch = React.useCallback(async (): Promise<void> => {
        try {
            setIsSearching(true);
            const response = await searchAnalyticsFilters(customerConfig, filterType, searchTerm, searchById);

            const searchResults: FilterOption[] = Array.isArray(response)
                ? response.map(item =>
                    typeof item === "string" ? item : { label: item.label, brandId: item.brand_id }
                  )
                : [];
            setOptions(searchResults);
            setHasMore(false);
        } catch (error) {
            Logger.error("Error searching filters", error);
        } finally {
            setIsSearching(false);
        }
    }, [customerConfig, filterType, searchTerm, searchById]);

    React.useEffect(() => {
        if (isOpen && options.length === 0 && !searchTerm) {
            loadFilterValues(1);
        }
    }, [isOpen, options.length, searchTerm, loadFilterValues]);

    React.useEffect(() => {
        if (isOpen && filterType === "brand_id") {
            setOptions([]);
            setCurrentPage(1);
            if (searchTerm) {
                handleSearch();
            } else {
                loadFilterValues(1);
            }
        }
    }, [searchById]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm) {
            searchTimeoutRef.current = setTimeout(() => {
                handleSearch();
            }, 300);
        } else if (isOpen) {
            setOptions([]);
            setCurrentPage(1);
            loadFilterValues(1);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, isOpen, handleSearch, loadFilterValues]);

    const handleLoadMore = (): void => {
        if (!isLoading && hasMore) {
            loadFilterValues(currentPage + 1);
        }
    };

    const handleToggle = (value: string): void => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter(v => v !== value));
        } else {
            onChange([...selectedValues, value]);
        }
    };

    const handleSelectAll = (): void => {
        const allValues = options.map(opt => getOptionValue(opt));
        if (selectedValues.length === options.length) {
            onChange([]);
        } else {
            onChange(allValues);
        }
    };

    const displayText = selectedValues.length === 0
        ? placeholder
        : selectedValues.length === 1
            ? (() => {
                const option = findOptionByValue(selectedValues[0]);
                return option ? getOptionDisplay(option) : selectedValues[0];
            })()
            : `${selectedValues.length} selected`;

    return (
        <div ref={dropdownRef} style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                <Form.Label style={{ marginBottom: 0 }}>{getDisplayLabel()}</Form.Label>
                <div style={{ display: "flex", gap: "4px" }}>
                    {filterType === "brand_id" && (
                        <Button
                            size="sm"
                            variant={"outline-secondary"}
                            onClick={() => setSearchById(!searchById)}
                            style={{
                                padding: "0px 6px",
                                fontSize: "11px",
                                lineHeight: "1.2",
                                height: "20px",
                                backgroundColor: "#e9ecef",
                                borderColor: "#ced4da",
                                color: "#6c757d",
                                display: "flex",
                                alignItems: "center"
                            }}
                            title={searchById ? "Switch to Brand Name" : "Switch to Brand ID"}
                        >
                            {searchById ? <Hash size={12} /> : <Type size={12} />}
                        </Button>
                    )}
                </div>
            </div>
            <Dropdown show={isOpen} onToggle={() => setIsOpen(!isOpen)}>
                <Dropdown.Toggle
                    variant="outline-secondary"
                    style={{
                        width: "100%",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        backgroundColor: "white",
                        color: "black"
                    }}
                >
                    {displayText}
                </Dropdown.Toggle>
                <Dropdown.Menu
                    className="bg-primary-transparent"
                    style={{
                        minWidth: filterType === "brand_id" || filterType === "attribution_id" ? "400px" : "100%",
                        maxWidth: "90vw",
                        maxHeight: "300px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "8px"
                    }}
                >
                    <Form.Control
                        type="text"
                        placeholder={getSearchPlaceholder()}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ marginBottom: "8px" }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                    {(isLoading || isSearching) && options.length === 0 && (
                        <div style={{ padding: "16px", textAlign: "center" }}>
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}
                    {!isLoading && !isSearching && options.length > 0 && (
                        <div
                            style={{
                                padding: "4px 8px",
                                borderBottom: "1px solid #eee",
                                marginBottom: "4px"
                            }}
                        >
                            <Form.Check
                                type="checkbox"
                                label={selectedValues.length === options.length ? "Deselect All" : "Select All"}
                                checked={selectedValues.length === options.length && options.length > 0}
                                onChange={handleSelectAll}
                                style={{ fontWeight: "bold" }}
                            />
                        </div>
                    )}
                    {!isLoading && !isSearching && options.length === 0 && (
                        <div style={{ padding: "8px", color: "#999", textAlign: "center" }}>
                            No options found
                        </div>
                    )}
                    {options.map(option => {
                        const value = getOptionValue(option);
                        const display = getOptionDisplay(option);
                        return (
                            <div
                                key={value}
                                style={{
                                    padding: "4px 8px",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word"
                                }}
                            >
                                <Form.Check
                                    type="checkbox"
                                    label={display}
                                    checked={selectedValues.includes(value)}
                                    onChange={() => handleToggle(value)}
                                />
                            </div>
                        );
                    })}
                    {hasMore && !searchTerm && (
                        <div
                            style={{
                                padding: "8px",
                                borderTop: "1px solid #eee",
                                marginTop: "4px",
                                textAlign: "center"
                            }}
                        >
                            <Button
                                variant="link"
                                size="sm"
                                onClick={handleLoadMore}
                                disabled={isLoading}
                                style={{ padding: "0", textDecoration: "none" }}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
                                        Loading...
                                    </>
                                ) : (
                                    "Load More"
                                )}
                            </Button>
                        </div>
                    )}
                </Dropdown.Menu>
            </Dropdown>
            {selectedValues.length > 0 && (
                <small style={{ color: "black" }}>
                    {selectedValues.length} selected
                </small>
            )}
        </div>
    );
};

/* eslint-disable camelcase */
type AttributionFunnelDataPoint = {
    datepartition: string;
    landings: number;
    engaged_sessions: number;
    video_starts: number;
    video_completions: number;
    identifications: number;
};
/* eslint-enable camelcase */

type AttributionFunnelProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type AttributionFunnelFilters = {
    startDate: string;
    endDate: string;
    attributionId: string[];
};

type AttributionFunnelState = {
    analyticsData: AttributionFunnelDataPoint[];
    isLoading: boolean;
    error: string | null;
    filters: AttributionFunnelFilters;
    appliedFilters: AttributionFunnelFilters;
    isDarkTheme: boolean;
    filterKey: number;
    isScrolled: boolean;
    selectedPreset: string;
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

class AttributionFunnel extends React.Component<AttributionFunnelProps, AttributionFunnelState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: AttributionFunnelProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AttributionFunnelFilters = {
            startDate,
            endDate,
            attributionId: []
        };

        this.state = {
            analyticsData: [],
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            isDarkTheme: true,
            filterKey: 0,
            isScrolled: false,
            selectedPreset: "W",
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
        window.addEventListener("scroll", this.handleScroll);
    }

    componentWillUnmount(): void {
        window.removeEventListener("scroll", this.handleScroll);
    }

    private handleScroll = (): void => {
        this.setState({ isScrolled: window.scrollY > 10 });
    };

    private handleDatePreset = (preset: string): void => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        let startDate: Date;
        let endDate: Date = yesterday;

        switch (preset) {
            case "3":
                startDate = new Date(yesterday);
                startDate.setDate(yesterday.getDate() - 2);
                break;
            case "W":
                startDate = new Date(yesterday);
                startDate.setDate(yesterday.getDate() - 6);
                break;
            case "M":
                startDate = new Date(yesterday);
                startDate.setMonth(yesterday.getMonth() - 1);
                break;
            case "LM":
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "Y":
                startDate = new Date(yesterday);
                startDate.setDate(yesterday.getDate() - 365);
                break;
            default:
                return;
        }

        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        };

        this.setState(prevState => ({
            filters: {
                ...prevState.filters,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate)
            },
            selectedPreset: preset
        }));
    };

    private fetchData = async (): Promise<void> => {
        try {
            this.setState({ isLoading: true, error: null });

            const { appliedFilters } = this.state;

            const response = await getAnalyticsWidget(
                this.props.customerConfig,
                "attribution_funnel",
                appliedFilters.startDate,
                appliedFilters.endDate,
                appliedFilters.attributionId.length > 0 ? appliedFilters.attributionId : undefined
            );

            const data = (response as any)?.result;
            const dataArray = Array.isArray(data) ? data : [];
            const meta = (response as any)?.widget;
            /* eslint-disable camelcase */
            const sidebarEntries: DocsSidebarEntry[] = meta ? [{
                widgetKey: "attribution_funnel",
                displayName: meta.display_name,
                description: meta.description,
                schema: (response as any)?.schema
            }] : [];
            /* eslint-enable camelcase */
            this.setState({ analyticsData: dataArray, sidebarEntries });
        } catch (err) {
            Logger.error("Error fetching attribution funnel data", err);
            this.setState({ error: "Failed to fetch attribution funnel data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private handleDateChange = (field: "startDate" | "endDate", value: string): void => {
        this.setState(prevState => ({
            filters: { ...prevState.filters, [field]: value }
        }));
    };

    private handleAttributionChange = (values: string[]): void => {
        this.setState(prevState => ({
            filters: { ...prevState.filters, attributionId: values }
        }));
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private convertToAnalyticsFilters = (): AnalyticsFilters => {
        const { appliedFilters } = this.state;
        return {
            startDate: appliedFilters.startDate,
            endDate: appliedFilters.endDate,
            attributionId: appliedFilters.attributionId,
            brandId: [],
            devicePlatform: []
        };
    };

    private getExportData = (): ExportableData[] => {
        const { analyticsData } = this.state;

        if (analyticsData.length === 0) {
            return [];
        }

        /* eslint-disable camelcase */
        return [{
            title: "Attribution Funnel",
            columns: [
                { key: "datepartition", label: "Date", format: "date" },
                { key: "landings", label: "Landings", format: "number" },
                { key: "engaged_sessions", label: "Engaged Sessions", format: "number" },
                { key: "video_starts", label: "Video Starts", format: "number" },
                { key: "video_completions", label: "Video Completions", format: "number" },
                { key: "identifications", label: "Identifications", format: "number" }
            ],
            rows: analyticsData,
            metadata: {
                filters: this.convertToAnalyticsFilters(),
                pageKey: "attribution_funnel",
                customerSlug: this.props.customerConfig.slug
            }
        }];
        /* eslint-enable camelcase */
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "Attribution Funnel",
            pageKey: "attribution_funnel",
            customerSlug: this.props.customerConfig.slug,
            filters: this.convertToAnalyticsFilters(),
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private renderSummaryBlocks(): ReactNode {
        const { analyticsData, isDarkTheme, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const schema = sidebarEntries[0]?.schema as Record<string, { description?: string }> | undefined;

        if (analyticsData.length === 0) {
            return null;
        }

        /* eslint-disable camelcase */
        const totalLandings = analyticsData.reduce((sum, d) => sum + d.landings, 0);
        const totalEngagedSessions = analyticsData.reduce((sum, d) => sum + d.engaged_sessions, 0);
        const totalVideoStarts = analyticsData.reduce((sum, d) => sum + d.video_starts, 0);
        const totalVideoCompletions = analyticsData.reduce((sum, d) => sum + d.video_completions, 0);
        const totalIdentifications = analyticsData.reduce((sum, d) => sum + d.identifications, 0);
        /* eslint-enable camelcase */

        const metrics: MetricCardItem[] = [
            { key: "landings", label: "Landings", value: totalLandings, description: schema?.["landings"]?.description },
            { key: "engaged_sessions", label: "Engaged Sessions", value: totalEngagedSessions, description: schema?.["engaged_sessions"]?.description },
            { key: "video_starts", label: "Video Starts", value: totalVideoStarts, description: schema?.["video_starts"]?.description },
            { key: "video_completions", label: "Video Completions", value: totalVideoCompletions, description: schema?.["video_completions"]?.description },
            { key: "identifications", label: "Identifications", value: totalIdentifications, description: schema?.["identifications"]?.description }
        ];

        return (
            <WidgetCard theme={theme}>
                <MetricCards metrics={metrics} theme={theme} />
            </WidgetCard>
        );
    }

    private renderChart(): ReactNode {
        const { analyticsData, isDarkTheme } = this.state;
        const theme = getWidgetTheme(isDarkTheme);

        const metricConfig: MetricConfig[] = [
            { key: "landings", name: "Landings", color: "#8884d8" },
            { key: "engaged_sessions", name: "Engaged Sessions", color: "#82ca9d" },
            { key: "video_starts", name: "Video Starts", color: "#ffc658" },
            { key: "video_completions", name: "Video Completions", color: "#ff7c7c" },
            { key: "identifications", name: "Identifications", color: "#06b6d4" }
        ];

        /* eslint-disable camelcase */
        const initialVisibleMetrics: Record<string, boolean> = {
            landings: true,
            engaged_sessions: true,
            video_starts: true,
            video_completions: true,
            identifications: true
        };
        /* eslint-enable camelcase */

        return (
            <WidgetCard
                title="Attribution Funnel Over Time"
                description={this.state.sidebarEntries[0]?.description}
                theme={theme}
                isEmpty={analyticsData.length === 0}
            >
                <InteractiveLineChart
                    title=""
                    data={analyticsData}
                    xAxisDataKey="datepartition"
                    metrics={metricConfig}
                    initialVisibleMetrics={initialVisibleMetrics}
                    theme={theme}
                />
            </WidgetCard>
        );
    }

    private hasChanges = (): boolean => {
        const { filters, appliedFilters } = this.state;
        return (
            filters.startDate !== appliedFilters.startDate ||
            filters.endDate !== appliedFilters.endDate ||
            JSON.stringify(filters.attributionId) !== JSON.stringify(appliedFilters.attributionId)
        );
    };

    private renderFilters(): ReactNode {
        const { filters, isScrolled, selectedPreset } = this.state;

        const hasChanges = this.hasChanges();
        const datePresets = ["3", "W", "M", "LM", "Y"];

        return (
            <div
                className={isScrolled ? "bg-primary-transparent" : ""}
                style={{
                    position: "sticky",
                    top: 56,
                    zIndex: 100,
                    backgroundColor: isScrolled ? undefined : "transparent",
                    paddingTop: "12px",
                    paddingBottom: "8px",
                    marginBottom: "2px",
                    marginLeft: "-15px",
                    marginRight: "-15px",
                    paddingLeft: "15px",
                    paddingRight: "15px",
                    width: "calc(100% + 30px)",
                    transition: "background-color 0.2s ease"
                }}
            >
                <Row className="align-items-start">
                    <Col md={2}>
                        <Form.Group>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                                <Form.Label style={{ marginBottom: 0 }}>Start Date</Form.Label>
                                <div style={{ display: "flex", gap: "4px" }}>
                                    {datePresets.map(preset => (
                                        <Button
                                            key={preset}
                                            size="sm"
                                            variant={selectedPreset === preset ? "secondary" : "outline-secondary"}
                                            onClick={() => this.handleDatePreset(preset)}
                                            style={{
                                                padding: "0px 5px",
                                                fontSize: "10px",
                                                lineHeight: "1.2",
                                                height: "20px",
                                                minWidth: "20px",
                                                ...(selectedPreset !== preset ? {
                                                    backgroundColor: "#e9ecef",
                                                    borderColor: "#ced4da",
                                                    color: "#6c757d"
                                                } : {})
                                            }}
                                            title={
                                                preset === "3" ? "Last 3 Days" :
                                                preset === "W" ? "Week" :
                                                preset === "M" ? "Month" :
                                                preset === "LM" ? "Last Month" :
                                                "Year"
                                            }
                                        >
                                            {preset}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            <Form.Control
                                type="date"
                                value={filters.startDate}
                                max={new Date(Date.now() - 86400000).toISOString().split("T")[0]}
                                onChange={(e) => this.handleDateChange("startDate", e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label style={{ marginBottom: "4px" }}>End Date</Form.Label>
                            <div title="Date selection is only possible for a full day's data.">
                                <Form.Control
                                    type="date"
                                    value={filters.endDate}
                                    max={new Date(Date.now() - 86400000).toISOString().split("T")[0]}
                                    onChange={(e) => this.handleDateChange("endDate", e.target.value)}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group>
                            <MultiSelectDropdown
                                label="Attribution ID"
                                selectedValues={filters.attributionId}
                                onChange={this.handleAttributionChange}
                                placeholder="All attributions"
                                customerConfig={this.props.customerConfig}
                                filterType="attribution_id"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label style={{ marginBottom: "4px" }}>&nbsp;</Form.Label>
                            <Button
                                variant={hasChanges ? "secondary" : "outline-secondary"}
                                onClick={this.handleApplyFilters}
                                disabled={!hasChanges}
                                style={{
                                    width: "100%",
                                    display: "block",
                                    ...(!hasChanges && {
                                        backgroundColor: "#e9ecef",
                                        borderColor: "#ced4da",
                                        color: "#6c757d",
                                        cursor: "not-allowed"
                                    })
                                }}
                            >
                                Apply Filters
                            </Button>
                        </Form.Group>
                    </Col>
                </Row>
            </div>
        );
    }

    render(): ReactNode {
        const { isLoading, error, isDarkTheme, sidebarOpen, sidebarEntries } = this.state;

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="attribution-funnel" style={{ position: "relative" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && this.state.analyticsData.length > 0}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleSidebar}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "20px",
                                padding: "4px 8px",
                                color: isDarkTheme ? "#ffffff" : "#000000"
                            }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                        <Button
                            onClick={this.toggleTheme}
                            style={{
                                backgroundColor: "transparent",
                                border: "none",
                                fontSize: "24px",
                                padding: "4px 8px",
                                color: isDarkTheme ? "#ffffff" : "#000000"
                            }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2 style={{ color: isDarkTheme ? "#ffffff" : "#000000" }}>Attribution Funnel</h2>
                        </Col>
                    </Row>

                    {this.renderFilters()}
                    <PopPeriodBanner startDate={this.state.appliedFilters.startDate} endDate={this.state.appliedFilters.endDate} />

                    <Row style={{ paddingTop: "24px" }}>
                        <Col md={12}>
                            {isLoading && (
                                <div className="text-center" style={{ padding: "40px" }}>
                                    <Spinner animation="border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </Spinner>
                                </div>
                            )}

                            {error && (
                                <Alert variant="danger">{error}</Alert>
                            )}

                            {!isLoading && !error && (
                                <>
                                    {this.renderSummaryBlocks()}
                                    {this.renderChart()}
                                </>
                            )}
                        </Col>
                    </Row>

                    <ScheduleExportModal
                        show={this.state.showScheduleModal}
                        onHide={() => this.setState({ showScheduleModal: false })}
                        filters={this.convertToAnalyticsFilters()}
                        pageKey="attribution_funnel"
                        pageTitle="Attribution Funnel"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={null}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
              </div>
              <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default AttributionFunnel;
